'use client';

// Package Imports
import React, { useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';

// Component Imports
import StylizedButtonLink from "@/components/stylizedButtonLink";
import Loader from "@/components/loader";
import Spinner from '@/components/spinner';

// Functions
import { calculateOrderTotals } from '@/lib/functions/calculateOrderTotals';
import { formatCurrency } from '@/lib/functions/formatCurrency';
import {
  isShippingQuoteUnavailable,
  shippingUnavailableTotalAdjustment,
} from '@/lib/functions/isShippingQuoteUnavailable';

// Styles
import styles from './cartCheckoutTotals.module.scss';

// Types
import type {OrderPayload, OrderPayloadPricing} from '@/lib/types/orderPayload';

interface CartCheckoutTotalsProps {
  pricing?: OrderPayloadPricing;
  state?: string;
  setIsModalOpen?: (isOpen: boolean) => void;
  onEditCart?: () => void;
  tipAmount?: number;
  className?: string;
  showBufferTotal?: boolean;
  orderData?: OrderPayload;
  loading?: boolean;
  onCouponRemoved?: () => Promise<void> | void;
}

const CartCheckoutTotals: React.FC<CartCheckoutTotalsProps> = ({
  pricing,
  state,
  setIsModalOpen = () => {},
  onEditCart,
  className = '',
  showBufferTotal = true,
  orderData,
  loading = false,
  onCouponRemoved,
}) => {
  const [removingCoupon, setRemovingCoupon] = useState<boolean>(false);

  const {
    appliedCouponCode = '',
  } = {
    appliedCouponCode: pricing?.appliedCouponCode ?? '',
  };
  const couponIsSet = '' !== appliedCouponCode;

  const removeCoupon = async () => {
    if (!orderData?.token) return;

    setRemovingCoupon(true);
    try {
      const response = await fetch(`/api/coupon/${orderData.token}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        if (onCouponRemoved) {
          await onCouponRemoved();
        }
      } else {
        console.error('Failed to remove coupon');
      }
    } catch (error) {
      console.error('Error removing coupon:', error);
    } finally {
      setRemovingCoupon(false);
    }
  };

  const hasFulfillmentCoupon =
    couponIsSet && (pricing?.order_shipping_promotion ?? 0) < 0;

  const hasSubtotalCoupon =
    couponIsSet &&
    (pricing?.order_shipping_promotion ?? 0) >= 0 &&
    (pricing?.promotions ?? 0) < 0;

  const shippingUnavailable = isShippingQuoteUnavailable(pricing?.shipping);
  const shippingTotalAdjustment = shippingUnavailableTotalAdjustment(pricing?.shipping);

  const { netTotal, authTotal } = calculateOrderTotals(pricing, showBufferTotal);
  const displayNetTotal = netTotal + shippingTotalAdjustment;
  const displayAuthTotal = authTotal + shippingTotalAdjustment;

  return (
    <section className={classNames(styles.cartCheckoutTotals, className && styles[className])}>
      {loading && <div
        className={styles.loader}
      >
        <Loader variant={"2"} size={"small"}/>
      </div>}

      <table role="grid" aria-label="Cart Totals">
        <tbody>
          <tr>
            <th scope='row' aria-label='Subtotal'>
              Subtotal{' '}
              <span className={styles.editCart}>
                {onEditCart ? (
                  <button type="button" onClick={onEditCart}>
                    Edit Cart
                  </button>
                ) : (
                  <Link href="/cart">Edit Cart</Link>
                )}
              </span>
            </th>
            <td data-th='Subtotal' itemProp="price" itemScope itemType="http://schema.org/Price">
              {formatCurrency(pricing?.subtotal, true)}
            </td>
          </tr>
          {hasSubtotalCoupon &&
            <tr>
              <th scope="row" aria-label="Coupon" className={styles.couponRow}>
                Coupon {appliedCouponCode}
                {removingCoupon ? (
                  <Spinner />
                ) : (
                  <button
                    type="button"
                    className={styles.removeCouponButton}
                    aria-label={`Remove coupon "${appliedCouponCode}"`}
                    onClick={removeCoupon}
                    title={`Remove coupon "${appliedCouponCode}"`}
                  >
                    ×
                  </button>
                )}
              </th>
              <td data-th="Coupon" itemProp="price" itemScope itemType="http://schema.org/Price">
                {formatCurrency(pricing?.promotions, true)}
              </td>
            </tr>
          }
          <tr>
            <th scope='row' aria-label='Tax'>
              Tax
            </th>
            <td data-th='Tax' itemProp="price" itemScope itemType="http://schema.org/Price">
              {(state !== 'cart')
                ? formatCurrency(pricing?.taxes, true)
                : ( <StylizedButtonLink
                  className={styles.estimateButton}
                  onClick={() => setIsModalOpen(true)}
                >
                      Estimate
                </StylizedButtonLink>
                )
              }
            </td>
          </tr>
          <tr>
            <th scope="row" aria-label="Shipping">
              Shipping
            </th>
            <td data-th="Shipping" itemProp="price" itemScope itemType="http://schema.org/Price">
              {(state !== 'cart')
                ? shippingUnavailable
                  ? <span className={styles.shippingUnavailable}>Unavailable</span>
                  : formatCurrency(pricing?.shipping, true)
                : ( <StylizedButtonLink
                  className={styles.estimateButton}
                  onClick={() => setIsModalOpen(true)}
                >
                        Estimate
                </StylizedButtonLink>
                )
              }
            </td>
          </tr>
          {hasFulfillmentCoupon &&
            <tr>
              <th scope="row" aria-label="Coupon" className={styles.couponRow}>
                Shipping Coupon {appliedCouponCode}
                {removingCoupon ? (
                  <Spinner />
                ) : (
                  <button
                    type="button"
                    className={styles.removeCouponButton}
                    aria-label={`Remove coupon "${appliedCouponCode}"`}
                    onClick={removeCoupon}
                    title={`Remove coupon "${appliedCouponCode}"`}
                  >
                    ×
                  </button>
                )}
              </th>
              <td data-th="Coupon" itemProp="price" itemScope itemType="http://schema.org/Price">
                {formatCurrency(pricing?.order_shipping_promotion, true)}
              </td>
            </tr>
          }
          <tr className={styles['total']}>
            <th scope='row' aria-label='Total'>
              Total
            </th>
            <td data-th='Total' itemProp="price" itemScope itemType="http://schema.org/Price">
              {formatCurrency(displayNetTotal, true)}
            </td>
          </tr>
          {showBufferTotal &&
          <tr className={styles['tempAuth']}>
            <th scope="row" aria-label="Delivery">
              Temporary Authorization Total
            </th>
            <td data-th="Delivery" itemProp="price" itemScope itemType="http://schema.org/Price">
              {formatCurrency(displayAuthTotal, true)}
            </td>
          </tr>
          }
        </tbody>
      </table>
    </section>
  );
};

export default CartCheckoutTotals;
